/**
 * Config Service
 *
 * Client-side service for site configuration.
 * Handles signup enabled/disabled status.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, onValue, type Unsubscribe } from 'firebase/database';
import { app, db } from '@/lib/firebase';

const functions = getFunctions(app);

/**
 * Get signup status via Cloud Function (one-time fetch)
 *
 * @returns Whether signups are enabled (defaults to true)
 */
export async function getSignupStatus(): Promise<boolean> {
  const getSignupStatusFn = httpsCallable<void, { signupEnabled: boolean }>(
    functions,
    'getSignupStatus'
  );

  try {
    const result = await getSignupStatusFn();
    return result.data.signupEnabled;
  } catch (error) {
    console.error('Error fetching signup status:', error);
    // Default to enabled to prevent accidental lockout
    return true;
  }
}

/**
 * Subscribe to signup status changes (real-time updates)
 *
 * @param callback Function called when signup status changes
 * @returns Unsubscribe function
 */
export function subscribeToSignupStatus(
  callback: (enabled: boolean) => void
): Unsubscribe {
  const configRef = ref(db, 'config/signupEnabled');

  return onValue(
    configRef,
    (snapshot) => {
      const signupEnabled = snapshot.val();
      // Default to enabled if config doesn't exist
      callback(signupEnabled !== false);
    },
    (error) => {
      console.error('Error subscribing to signup status:', error);
      // Default to enabled on error
      callback(true);
    }
  );
}

/**
 * Set signup enabled status (admin only)
 *
 * @param enabled Whether to enable or disable signups
 * @returns Success result
 */
export async function setSignupEnabled(
  enabled: boolean
): Promise<{ success: boolean; signupEnabled: boolean }> {
  const setSignupEnabledFn = httpsCallable<
    { enabled: boolean },
    { success: boolean; signupEnabled: boolean }
  >(functions, 'setSignupEnabled');

  const result = await setSignupEnabledFn({ enabled });
  return result.data;
}
